import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import CustomDomainManager from '@/components/CustomDomainManager';

export default function StoreEditor() {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  
  useEffect(() => {
    fetchData();
  }, [storeId]);
  
  useEffect(() => {
    if (store) {
      document.title = `${store.name} Settings - Fluencer`;
    }
  }, [store]);
  
  const fetchData = async () => {
    try {
      const storeRes = await fetch(`${API}/stores/${storeId}`, { credentials: 'include' });
      
      if (!storeRes.ok) throw new Error('Store not found');
      
      const storeData = await storeRes.json();
      
      setStore(storeData);
      setName(storeData.name);
      setDescription(storeData.description || '');
    } catch (error) {
      toast.error('Failed to load store');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/stores/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name,
          description: description || null,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to update store');
      
      toast.success('Store updated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handlePublish = async () => {
    try {
      const response = await fetch(`${API}/stores/${storeId}/publish`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to publish store');
      
      toast.success('Store published successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-store-button"
              variant="ghost"
              size="sm"
              className="rounded-full"
              onClick={() => navigate(`/store/${storeId}/products`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-outfit font-bold">Store Settings</h1>
              <p className="text-muted-foreground">{store?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {store?.is_published && (
              <Button
                data-testid="view-live-store-button"
                variant="outline"
                className="rounded-full"
                onClick={() => window.open(`/s/${store.slug}`, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Live
              </Button>
            )}
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-8 space-y-8">
          {/* Basic Info */}
          <div className="space-y-4">
            <h2 className="text-xl font-outfit font-bold">Basic Information</h2>
            <div>
              <Label htmlFor="store-name">Store Name</Label>
              <Input
                id="store-name"
                data-testid="store-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="store-description">Description</Label>
              <Input
                id="store-description"
                data-testid="store-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Store URL</Label>
              <div className="mt-2 p-3 bg-muted rounded-lg font-mono text-sm">
                {window.location.origin}/s/{store?.slug}
              </div>
            </div>
          </div>
          
          {/* Custom Domain */}
          <div className="space-y-4">
            <CustomDomainManager 
              storeId={storeId}
              domains={store?.custom_domains || []}
              onUpdate={fetchData}
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              data-testid="save-store-button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            {!store?.is_published && (
              <Button
                data-testid="publish-store-button"
                onClick={handlePublish}
                variant="outline"
                className="rounded-full"
              >
                Publish Store
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
